import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from "@tanstack/react-query"
import { ImageBody } from "./api"
import ImageSelection from "./ImageSelection"
import { Button } from "../ui/button.tsx"
import { useAtomValue } from "jotai"
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms"
import ImagePreview from "./ImagePreview"
import { useContext, useEffect } from "react"
import { GalleryPreviewContext, GalleryPreviewContextType } from "./context"

type Props<T> = {
  selectedImage: number | undefined
  hasNextPage: boolean,
  onSelect: (image: number) => void,
  galleryImages?: ImageBody[],
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<
    InfiniteQueryObserverResult<InfiniteData<{ data: T[] }, unknown>, Error>
  >;
  searchValue: string
}

const getFilteredImages = (value: string, images: ImageBody[] | undefined): ImageBody[] | undefined => {
  if (!images) {
    return [];
  }

  const filteredImages: ImageBody[] = images.filter(img => ( img.prompt !== null && img.prompt.toLowerCase().includes(value) ))

  return filteredImages
}

const ImageGrid = <T,>({ selectedImage, onSelect, galleryImages, fetchNextPage, searchValue, hasNextPage }: Props<T>) => {

  const theme = useAtomValue(themeAtom)

  const { openPreview, setImages } = useContext<GalleryPreviewContextType>(GalleryPreviewContext)

  useEffect(() => {
    setImages(galleryImages ? galleryImages : [])
  }, [galleryImages, setImages])

  return (
    <div className="size-full">
      {galleryImages && <p className="text-sm text-gray-500 font-poppins pb-3">{getFilteredImages(searchValue, galleryImages)?.length} Images found</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {getFilteredImages(searchValue, galleryImages)?.map((image, index) => (
          <ImageSelection
            key={index}
            isSelected={selectedImage === image.id}
            onSelect={onSelect}
            image={image} />
        ))}
      </div>
      <div className="w-full py-6 flex items-center justify-center">
        {hasNextPage &&
          <Button
            onClick={() => fetchNextPage()}
            style={{
              backgroundColor: theme.secondary
            }}>View More
          </Button>
        }
      </div>
      {openPreview && <ImagePreview onSelect={onSelect} selectedImage={selectedImage} />}
    </div>
  )
}

export default ImageGrid
